import { RefObject, createRef, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { CmsLink } from '../../../cmsTypes/cmsLink'
import { cmsDataService } from '../../../services/cms.data.service'
import { DropdownOption } from '@vista-digital/occ-ui'

interface NavItem extends CmsLink {
    isCollapsed: boolean
    ref: RefObject<HTMLDivElement>
}

const ZERO_WIDTH_SPACE = '\u200B'
const MOBILE_BREAKPOINT = 992
const MORE_BUTTON_ESTIMATED_WIDTH = 100

export const useCollapsibleNavItems = (
    navContainer: RefObject<HTMLDivElement>,
    moreButton: RefObject<HTMLDivElement>
) => {
    const { mainNavigation } = cmsDataService.get().header

    // Stable refs for each nav item - created once and reused across renders
    const itemRefs = useRef(mainNavigation.map(() => createRef<HTMLDivElement>()))
    const [collapsedIndices, setCollapsedIndices] = useState<Set<number>>(new Set())

    const recalculate = useCallback(() => {
        const container = navContainer.current
        if (!container || container.offsetWidth === 0) return

        // Don't collapse on mobile - the burger menu handles navigation
        if (window.innerWidth < MOBILE_BREAKPOINT) {
            setCollapsedIndices(prev => (prev.size === 0 ? prev : new Set()))
            return
        }

        // Measure all item widths. This works even for collapsed items since they use
        // position: absolute (not display: none) and retain their intrinsic width.
        const widths = itemRefs.current.map(ref => ref.current?.offsetWidth ?? 0)
        if (widths.some(w => w === 0)) return // DOM not ready yet

        const containerWidth = container.offsetWidth
        const totalWidth = widths.reduce((sum, w) => sum + w, 0)

        // If everything fits without the More button, show all items
        if (totalWidth <= containerWidth) {
            setCollapsedIndices(prev => (prev.size === 0 ? prev : new Set()))
            return
        }

        // Some items need collapsing - account for the More button width.
        // Use actual measured width if the More button is in the DOM, otherwise estimate.
        const moreWidth = moreButton.current?.offsetWidth || MORE_BUTTON_ESTIMATED_WIDTH
        const availableWidth = containerWidth - moreWidth

        const newCollapsed = new Set<number>()
        let usedWidth = 0

        for (let i = 0; i < widths.length; i++) {
            if (usedWidth + widths[i] <= availableWidth) {
                usedWidth += widths[i]
            } else {
                newCollapsed.add(i)
            }
        }

        // Always keep the first item visible
        newCollapsed.delete(0)

        setCollapsedIndices(prev => {
            if (newCollapsed.size === prev.size && [...newCollapsed].every(i => prev.has(i))) {
                return prev
            }
            return newCollapsed
        })
    }, [navContainer, moreButton])

    // Synchronous layout calculation on mount to avoid a flash of uncollapsed items
    useLayoutEffect(() => {
        recalculate()
    }, [recalculate])

    // When the More button appears or disappears, recalculate with its actual measured width
    const hasCollapsed = collapsedIndices.size > 0
    useEffect(() => {
        requestAnimationFrame(recalculate)
    }, [hasCollapsed, recalculate])

    // Observe the nav container for size changes (handles window resize and layout shifts)
    useEffect(() => {
        const container = navContainer.current
        if (!container) return

        const observer = new ResizeObserver(() => recalculate())
        observer.observe(container)
        return () => observer.disconnect()
    }, [recalculate])

    // Build the nav items array with current collapsed state and stable refs
    const navItems: NavItem[] = mainNavigation.map((item, i) => ({
        ...item,
        isCollapsed: collapsedIndices.has(i),
        ref: itemRefs.current[i],
    }))

    const moreDropdownOptions: DropdownOption[] = navItems
        .filter(item => item.isCollapsed)
        .map(item => ({
            text: item.name,
            value: `${item.url}${ZERO_WIDTH_SPACE}${item.target}`,
        }))

    return {
        navItems,
        moreDropdownOptions,
    }
}
